import React, { useState, useEffect } from "react";
import { CardContent } from "@/components/ui/card";
import { useTenancy } from "@/contexts/TenancyContext";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import "./widgets.css";
import renderDate, { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";

interface Objective {
  objectiveID: string | number;
  objectiveText: string;
  targetDate: Date | string;
  owner?: string;
  addedBy?: string;
  dateAdded?: Date | string;
  dateUpdated?: Date | string;
  updatedBy?: string;
}

const ISMSObjectivesListWidget: React.FC = () => {
  const { activeTenancy } = useTenancy();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [collectionTriggered, setCollectionTriggered] = useState(false);

  // Report widget status to dashboard context
  useWidgetStatusReporter('isms-objectives-list', isLoading, error, objectives.length > 0);

  // Format date for display
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    return isNaN(dateObj.getTime()) ? 'Invalid date' : format(dateObj, 'MMM d, yyyy');
  };

  // Handle API response
  const handleApiResponse = (response: any) => {
    const widgetData = response.data?.objectives?.data;
    const metadata = response.metadata;
    
    if (widgetData?.objectives) {
      // Transform dates from strings to Date objects for sorting
      const formattedObjectives = widgetData.objectives.map((obj: any) => ({
        ...obj,
        targetDate: cleanDateAndTimeObject(obj.targetDate)
      }));
      
      // Sort objectives by target date (ascending)
      formattedObjectives.sort((a: any, b: any) => 
        new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime()
      );
      
      setObjectives(formattedObjectives);
      
      if (widgetData.lastUpdated) {
        setLastUpdated(cleanDateAndTimeObject(widgetData.lastUpdated).toLocaleString());
      } else if (metadata?.lastUpdated) {
        setLastUpdated(cleanDateAndTimeObject(metadata.lastUpdated).toLocaleString());
      }
      
      return true;
    }
    
    return false;
  };

  // Fetch objectives data
  useEffect(() => {
    if (!activeTenancy.id || activeTenancy.id === "" || activeTenancy.id === "Loading...") {
      return;
    }

    const fetchObjectivesData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await deiterateAPI.GET(
          `/dashboard/data?customerId=${activeTenancy.id}&widgetType=objectives`
        );
        
        const success = handleApiResponse(response);
        
        if (!success) {
          console.warn('No objectives data found in response');
          setError('No objectives data available');
        }
      } catch (err: any) {
        console.error('Error fetching objectives data:', err);
        
        if (err.response?.status === 404) {
          setCollectionTriggered(err.response.data?.collectionTriggered || false);
          setError('Objectives data not found. Please try again later.');
        } else {
          setError('Failed to load objectives data');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Set up polling every 30 seconds
    const pollInterval = 30000;
    let pollTimer: NodeJS.Timeout;
    
    // Initial fetch
    fetchObjectivesData();
    
    // Set up polling if collection was triggered
    if (collectionTriggered) {
      pollTimer = setInterval(fetchObjectivesData, pollInterval);
    }
    
    return () => {
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [activeTenancy.id, collectionTriggered]);

  // Loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-4">ISMS Objective Targets</h3>
        <div className="w-full space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </CardContent>
    );
  }

  // Error state
  if (error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4 text-center" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">ISMS Objective Targets</h3>
        <div className="text-destructive text-sm">{error}</div>
        {lastUpdated && (
          <div className="text-xs text-muted-foreground mt-2">
            Last updated: {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size' }}>
      <div className="w-full flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold">ISMS Objective Targets</h3>
        {lastUpdated && (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {/* {toLocalDateString(cleanDateAndTimeObject(lastUpdated), true)} */}
            {new Date(lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
      
      {objectives.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-4xl mb-2">🎯</div>
          <p className="text-sm text-muted-foreground">No ISMS Objectives</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="text-sm text-muted-foreground mb-2">
            Total Objectives: {objectives.length}
          </div>
          
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto pr-1 -mr-1">
              <Table className="table min-w-full divide-y divide-gray-200">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </TableHead>
                    <TableHead className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Objective
                    </TableHead>
                    <TableHead className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Target Date
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-200">
                  {objectives.map((obj) => {
                    const targetDate = obj.targetDate instanceof Date ? obj.targetDate : cleanDateAndTimeObject(obj.targetDate);
                    return (
                      <TableRow key={obj.objectiveID} className="hover:bg-gray-50">
                        <TableCell className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {obj.objectiveID}
                        </TableCell>
                        <TableCell className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate">
                          {obj.objectiveText}
                        </TableCell>
                        <TableCell className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {targetDate.toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}
    </CardContent>
  );
};

export default ISMSObjectivesListWidget;
